<template>
  <div
    ref="container"
    :class="b('', modifiers)"
    :style="styles"
  >
    <component
      :is="scrollable ? 'e-scrollable' : 'div'"
      :class="b('scrollbar')"
    >
      <ul :class="b('list')">
        <li
          v-for="(item, index) in sections"
          :key="`section--${index}`"
          :class="b('item', { active: sticky ? activeSectionIndex === index : item.active })"
        >
          <a
            :class="b('link', { active: sticky ? activeSectionIndex === index : item.active })"
            :href="item.isExternal ? item.url : `#${item.url}`"
          >
            {{ item.title }}
          </a>
        </li>
      </ul>
    </component>
  </div>
</template>

<script lang="ts">
  import { PropType, Ref, defineComponent, ref } from 'vue';
  import eScrollable from '@/elements/e-scrollable.vue';
  import { Modifiers } from '@/plugins/vue-bem-cn/src/globals';

  export type ContentSection = {
    title: string;
    url: string;
    isExternal?: boolean;
    active?: boolean;
  };

  type Setup = {
    container: Ref<HTMLDivElement | null | undefined>;
  };

  type Data = {
    activeSectionIndex: number;
    stickyBarHeight: number;
    stickyBarOffset: number;
    stickyAnchorOffset: number;
    contentSpacing: number;
  };

  /**
   * Renders a in page navigation bar.
   */
  export default defineComponent({
    name: 'c-content-navigation',
    components: {
      eScrollable,
    },

    // components: {},

    props: {
      /**
       * Sections to be displayed.
       */
      sections: {
        type: Array as PropType<ContentSection[]>,
        default: () => [],
      },

      /**
       * Define if the component should be sticky.
       */
      sticky: {
        type: Boolean,
        default: false,
      },

      /**
       * Define if the content navigation should be scrollable.
       */
      scrollable: {
        type: Boolean,
        default: false,
      },
    },
    emits: {
      'update:anchorOffset': (offset: number): boolean => typeof offset === 'number',
    },

    setup(): Setup {
      return {
        container: ref(),
      };
    },
    data(): Data {
      return {
        activeSectionIndex: 0,
        stickyBarHeight: 0,
        stickyBarOffset: 0,
        stickyAnchorOffset: 0,
        contentSpacing: 10, // Value in px to have some spacing to the content.
      };
    },

    computed: {
      modifiers(): Modifiers {
        const { sticky, scrollable } = this;

        return {
          sticky,
          scrollable,
        };
      },

      styles(): Record<string, string> {
        return {
          '--c-content-navigation-sticky-bar-offset': `${this.stickyBarOffset}px`,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      if (this.sticky) {
        this.updateStickySettings();

        window.addEventListener('scroll', this.updateActive);
        window.addEventListener('resizeend', this.updateStickySettings);
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      if (this.sticky) {
        window.removeEventListener('resizeend', this.updateStickySettings);
        window.removeEventListener('scroll', this.updateActive);
      }
    },
    // unmounted() {},

    methods: {
      /**
       * Updates all sticky settings for the navbar.
       */
      updateStickySettings() {
        this.stickyBarHeight = this.container?.clientHeight || 0;
        this.stickyBarOffset = document.querySelector('header')?.clientHeight || 0;
        this.stickyAnchorOffset = (this.stickyBarOffset + this.stickyBarHeight + this.contentSpacing) * -1;

        this.$emit('update:anchorOffset', this.stickyAnchorOffset);
      },

      /**
       * Determine the active navigation entry based on the scroll position.
       */
      updateActive() {
        const imaginaryScreenTop = this.contentSpacing;

        let lastTop = Number.POSITIVE_INFINITY;
        let lastMatch = -1;

        this.sections.forEach((section, index) => {
          const sectionTop = document.getElementById(section.url)?.getBoundingClientRect()?.top || 0;
          const isFirstItem = index === 0 && sectionTop > imaginaryScreenTop && sectionTop < lastTop;
          const isDefaultItem = sectionTop < imaginaryScreenTop;
          const isLastItem = this.sections.length - 1 === index && sectionTop > 0 && sectionTop < lastTop;

          if (isFirstItem || isDefaultItem || isLastItem) {
            lastTop = sectionTop;
            lastMatch = index;
          }
        });

        // Check if the user has scrolled to the end of the page
        const scrollPosition = window.scrollY + window.innerHeight;
        const pageHeight = document.documentElement.scrollHeight;

        if (scrollPosition >= pageHeight - this.contentSpacing) {
          lastMatch = this.sections.length - 1;
        }

        if (this.activeSectionIndex !== lastMatch) {
          this.activeSectionIndex = lastMatch;

          this.$nextTick(() => {
            this.onScrollToActiveTab();
          });
        }
      },

      /**
       * Scroll the active navigation item into view
       */
      onScrollToActiveTab() {
        const activeNavItem = this.container?.querySelector('.c-content-navigation__item--active');

        if (activeNavItem) {
          activeNavItem.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-content-navigation {
    $this: &;

    background-color: var(--theme-color-grayscale--1000);
    color: var(--theme-color-grayscale--100);
    border-bottom: 2px solid var(--theme-color-grayscale--500);
    overflow: hidden;

    &--sticky {
      position: sticky;
      top: var(--c-content-navigation-sticky-bar-offset);
      z-index: map.get(variables.$va-z-index, 'navigation') - 1;
      padding-top: 1.5rem;
    }

    &--scrollable {
      #{$this}__list {
        align-items: start;
      }

      #{$this}__item {
        width: 100%;
        min-width: 15rem;
      }
    }

    &__scrollbar {
      background-color: var(--theme-color-grayscale--950);

      .simplebar-track.simplebar-horizontal {
        bottom: -4px;
      }
    }

    &__list {
      display: flex;
      flex-flow: row nowrap;
      align-items: stretch;
    }

    &__item {
      display: flex;
      align-items: center;
      min-width: 8rem;
      max-width: 16rem;
      background-color: var(--theme-color-grayscale--950);

      &--active {
        background: var(--theme-color-grayscale--300);
      }
    }

    &__link {
      @include mixins.hyphens();
      @include mixins.hover-effect--default();

      position: relative;
      width: 100%;
      display: block;
      padding: 1rem 0.75rem;
      color: inherit;
      text-decoration: none;
      text-align: center;

      &--active {
        color: var(--theme-color-grayscale--950);
      }
    }
  }
</style>
